import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../../../Tw';
import { dimensionColors } from '../../../../common/qualitativePalettes';
import { ConvertedLocationFeature } from '../converter.serializable.model';
import { converterHighlightedRackTypes } from '../store/converter.layout.state';
import {
  converterColorDenominator,
  converterColorMode,
  converterGapThreshold,
} from '../store/converter.state';

export type ConverterLocationColorOut = [
  getColor: (loc: ConvertedLocationFeature) => [string, string] | null,
  getGradientColors: (loc: ConvertedLocationFeature) => string[],
];

const colors = TwTheme.extend.colors;

export type ConverterLocationColorParams = {
  locations?: ConvertedLocationFeature[];
};

// Simple direct color palette to ensure compatibility
const COLOR_PALETTES = {
  // Fixed colors for sequence patterns - no nested objects
  gap: colors.converterGaps.gap, // Pure red for gaps
  majorGap: colors.converterGaps.majorGap, // Darker red for major gaps
  even: colors.converterGaps.even, // Bright blue for even
  odd: colors.converterGaps.odd, // Bright green for odd
  unknown: colors.converterGaps.unknown, // Gray
  none: colors.converterGaps.none, // Default Location
  highlighted: colors.converterGaps.highlighted, // Magenta for highlighted racks
};

const useConverterLocationColor = (
  params: ConverterLocationColorParams = {},
): ConverterLocationColorOut => {
  const { locations = [] } = params;
  const highlightedRackTypes = useRecoilValue(converterHighlightedRackTypes);
  const colorDenominator = useRecoilValue(converterColorDenominator);
  const colorMode = useRecoilValue(converterColorMode);
  const gapThreshold = useRecoilValue(converterGapThreshold);

  // Cache for sequence analysis
  const [locationIdCache, setLocationIdCache] = useState<{
    ids: number[];
    patternMap: Map<
      string,
      {
        isEven: boolean;
        sequenceGap: number;
        allSequenceGap?: number;
      }
    >;
  }>({ ids: [], patternMap: new Map() });

  // Analyze all location IDs to detect patterns and gaps
  const analyzeLocationSequences = (locs: ConvertedLocationFeature[]) => {
    if (!locs || locs.length === 0) return { ids: [], patternMap: new Map() };

    // console.log('Analyzing sequences for', locs.length, 'locations');

    // Build a mapping of location ID to numeric part for quick lookup
    const locationIdToNumeric = new Map<string, number>();

    // Make sure locs is an array and filter out undefined items
    const validLocs = Array.isArray(locs) ? locs.filter(Boolean) : [];

    const locationsWithIds = validLocs
      .map(loc => {
        // Skip if loc is null or undefined or locationId is missing
        if (!loc || !loc.locationId) return null;

        const numericPart = loc.locationId.replace(/[^0-9]/g, '');
        const numericId = parseInt(numericPart, 10);
        if (!isNaN(numericId)) {
          locationIdToNumeric.set(loc.locationId, numericId);
        }
        return {
          locationId: loc.locationId,
          numericId,
          isEven: numericId % 2 === 0,
        };
      })
      .filter(item => item !== null && !isNaN(item.numericId));

    // Replace with a much simpler gap detection approach

    // First sort all locations by their numeric ID
    const sortedLocations = [...locationsWithIds].sort(
      (a, b) => a.numericId - b.numericId,
    );

    console.log('*** SORTED LOCATIONS FOR GAP DETECTION ***');
    console.table(
      sortedLocations.map(l => ({
        id: l.locationId,
        numericId: l.numericId,
        isEven: l.isEven,
      })),
    );

    // Create pattern map to store gap information
    const patternMap = new Map();

    // Process all items to detect gaps
    for (let i = 1; i < sortedLocations.length; i++) {
      const current = sortedLocations[i];
      const prev = sortedLocations[i - 1];
      const diff = current.numericId - prev.numericId;

      // Record whether locations are even/odd
      patternMap.set(current.locationId, {
        isEven: current.isEven,
        sequenceGap: 0,
        allSequenceGap: 0,
      });

      // If there's a gap, mark it on the current location
      if (diff > 1) {
        const gapSize = diff - 1;
        console.log(
          `*** FOUND GAP: ${prev.locationId}(${prev.numericId}) -> ${current.locationId}(${current.numericId}): ${gapSize} missing ***`,
        );

        // Store the gap data in the pattern map for both current and previous locations
        patternMap.set(current.locationId, {
          isEven: current.isEven,
          sequenceGap: 0,
          allSequenceGap: gapSize,
        });

        // Also mark the previous location to show the gap on both sides
        patternMap.set(prev.locationId, {
          isEven: prev.isEven,
          sequenceGap: 0,
          allSequenceGap: gapSize,
        });
      }
    }

    return {
      ids: sortedLocations.map(loc => loc.numericId),
      patternMap,
    };
  };

  // Standard denominator-based coloring
  const getDenominatorColor = (
    loc: ConvertedLocationFeature,
  ): [string, string] => {
    if (_.includes(highlightedRackTypes, loc.locationRackingType)) {
      return [COLOR_PALETTES.highlighted, null];
    }

    const numericPartLocationId = loc.locationId.replace(/[^0-9]/g, '');

    // If no denominator, use location order to generate a color
    if (!colorDenominator) {
      // Use location order for consistent coloring
      const size = _.values(dimensionColors).length;
      const index = +loc.locationOrder % size;
      const color = dimensionColors[index];
      return [color, null];
    }

    // Apply modulo operation with the denominator
    const value = +numericPartLocationId % colorDenominator;
    if (_.isNaN(value) || _.isNull(value)) return [COLOR_PALETTES.none, null];

    // Use direct HSL for more distinctive colors
    const hue = Math.floor((360 * value) / colorDenominator);
    return [`hsl(${hue}, 85%, 55%)`, null];
  };

  // Sequence mode to detect gaps in numeric sequence based on threshold
  const getSequenceColor = (
    loc: ConvertedLocationFeature,
  ): [string, string] => {
    if (_.includes(highlightedRackTypes, loc.locationRackingType)) {
      return [COLOR_PALETTES.highlighted, null];
    }

    const numericPartLocationId = loc.locationId.replace(/[^0-9]/g, '');
    const numericId = parseInt(numericPartLocationId, 10);

    if (isNaN(numericId)) return [COLOR_PALETTES.unknown, null];

    // Check for pattern info
    const patternInfo = locationIdCache.patternMap.get(loc.locationId);

    // Only log locations with gaps to reduce console spam
    if (patternInfo && patternInfo.allSequenceGap > 0) {
      console.log(
        `Location ${loc.locationId} (${numericId}) has gap: ${patternInfo.allSequenceGap}`,
      );
    }

    // Gap detection logic
    if (patternInfo && patternInfo.allSequenceGap !== undefined) {
      const gapSize = patternInfo.allSequenceGap;

      // If gap size is greater than threshold, highlight it
      if (gapSize > gapThreshold) {
        // Log the gap for debugging
        console.log(
          `HIGHLIGHTING GAP at ${loc.locationId}: gap ${gapSize} > threshold ${gapThreshold}`,
        );

        // Larger gaps get deeper red
        if (gapSize >= 10) {
          return [COLOR_PALETTES.majorGap, 'black'];
        }

        // Normal gaps get bright red
        return [COLOR_PALETTES.gap, 'black'];
      }
    }

    // No significant gap - fall back to even/odd coloring
    const isEven = patternInfo ? patternInfo.isEven : numericId % 2 === 0;
    return isEven ? [COLOR_PALETTES.even, null] : [COLOR_PALETTES.odd, null];
  };

  // No special coloring
  const getNoColor = (loc: ConvertedLocationFeature): [string, string] => {
    if (_.includes(highlightedRackTypes, loc.locationRackingType)) {
      return [COLOR_PALETTES.highlighted, null];
    }
    return [COLOR_PALETTES.none, null];
  };

  // Main color function that switches based on mode
  const getColor = (loc: ConvertedLocationFeature): [string, string] => {
    // Defensive check for null/undefined location
    if (!loc || typeof loc !== 'object') return [COLOR_PALETTES.none, null];

    // Ensure locationId exists
    if (!loc.locationId) return [COLOR_PALETTES.none, null];

    try {
      let result: [string, string] | null = null;

      switch (colorMode) {
        case 'sequence':
          result = getSequenceColor(loc);
          break;
        case 'denominator':
          result = getDenominatorColor(loc);
          break;
        case 'none':
        default:
          result = getNoColor(loc);
          break;
      }

      // Always ensure we return a valid color array
      if (!result || !Array.isArray(result) || result.length < 2) {
        console.warn(
          'Invalid color result for',
          loc.locationId,
          '- using fallback',
        );
        return [COLOR_PALETTES.none, null];
      }

      // console.log(`Color for ${loc.locationId}: ${result[0]}, ${result[1]}`);
      return result;
    } catch (error) {
      console.error(
        'Error determining color for location:',
        loc.locationId || 'unknown',
        error,
      );
      return [COLOR_PALETTES.none, null]; // Fallback
    }
  };

  const getGradientColors = (loc: ConvertedLocationFeature): string[] => {
    // Implementation for gradient colors if needed
    return [];
  };

  // Analyze sequences when locations or mode changes
  useEffect(() => {
    // Ensure locations is an array and not null/undefined
    const locationsArray = Array.isArray(locations)
      ? locations.filter(Boolean)
      : [];

    if (colorMode === 'sequence' && locationsArray.length > 0) {
      console.log(
        `Analyzing ${locationsArray.length} locations for sequence patterns with threshold ${gapThreshold}`,
      );
      try {
        const analyzed = analyzeLocationSequences(locationsArray);
        setLocationIdCache(analyzed);
      } catch (error) {
        console.error('Error analyzing location sequences:', error);
      }
    }
  }, [locations, colorMode, gapThreshold]);

  return [getColor, getGradientColors];
};

export default useConverterLocationColor;
